<template>
  <section class="estimator-submit mt-4" data-testid="estimator-submit-read">
    <SoAlert
      v-if="submitted"
      type="success"
      :text="SUBMITTED_MESSAGE"
      data-testid="estimator-submit-success"
    />

    <template v-else>
      <SoAlert
        v-if="errorMessage"
        type="error"
        :text="errorMessage"
        class="mb-2"
        data-testid="estimator-submit-error"
      />

      <SoButton
        v-if="!confirming"
        title="Submit as estimated read"
        type="primary"
        data-testid="estimator-submit-start"
        @click="startConfirming"
      />

      <!--
        Writing a read to the account is the one thing on this form that cannot
        be undone from Nova, so the agent sees exactly what Junifer will get
        before anything is sent.
      -->
      <div
        v-else
        class="estimator-submit__confirm"
        data-testid="estimator-submit-confirm"
      >
        <p class="mb-2">
          Submit to Junifer as a System read, quality Estimated, dated
          {{ targetDateLabel }}:
        </p>
        <p
          v-for="line in readLines"
          :key="line"
          class="estimator-submit__read mb-1"
          data-testid="estimator-submit-read-line"
        >
          {{ line }}
        </p>
        <p class="text-caption mt-3 mb-1">Junifer will record:</p>
        <pre
          class="estimator-submit__description text-caption"
          data-testid="estimator-submit-description"
          >{{ description }}</pre>
        <div class="d-flex mt-3">
          <SoButton
            title="Submit estimate"
            type="primary"
            class="mr-2"
            :loading="submitting"
            :disabled="submitting || !readings"
            data-testid="estimator-submit-confirm-btn"
            @click="submit"
          />
          <SoButton
            title="Cancel"
            type="text"
            :disabled="submitting"
            data-testid="estimator-submit-cancel-btn"
            @click="confirming = false"
          />
        </div>
      </div>
    </template>
  </section>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { isEqual } from "lodash"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import { IRegister } from "@/models/meterRegister/meterRegister.types"
  import { MeterReading } from "@/api/backofficeRest/generated/model"
  import { MeterReadEstimatorService } from "@/services/MeterReadEstimatorService"
  import { AuthService } from "@/services/AuthService"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { queryClient } from "@/api/utils/useQuery"
  import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
  import { IEstimatorResultRow } from "./estimatorResultRows"
  import {
    estimatedReadDescription,
    toEstimatedReadingInputs,
  } from "./estimatorSystemRead"

  export const SUBMITTED_MESSAGE =
    "Estimated read submitted to Junifer as a System read. It can take a few minutes to appear in the reading history."
  export const NO_METER_MESSAGE =
    "A register on this meter point cannot be traced to its meter, so the read cannot be submitted from here."

  export default defineComponent({
    components: { SoAlert, SoButton },
    props: {
      rows: {
        type: Array as PropType<IEstimatorResultRow[]>,
        required: true,
      },
      registers: {
        type: Array as PropType<IRegister[]>,
        required: true,
      },
      meterpoint: {
        type: Object as PropType<IMeterpoint>,
        required: true,
      },
      /** The date the estimate was made for, as sent to the estimator. */
      targetDate: {
        type: String,
        required: true,
      },
      calculationDetail: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        SUBMITTED_MESSAGE,
        confirming: false,
        submitting: false,
        submitted: false,
        errorMessage: "",
      }
    },
    computed: {
      readings(): MeterReading[] | null {
        return toEstimatedReadingInputs(
          this.rows,
          this.registers,
          this.meterpoint,
          this.targetDate,
        )
      },
      targetDateLabel(): string {
        return this.rows[0]?.targetDateLabel ?? this.targetDate
      },
      readLines(): string[] {
        return this.rows.map((row) =>
          this.rows.length > 1
            ? `${row.registerLabel} register: ${row.read}`
            : `Read: ${row.read}`,
        )
      },
      /** A preview: the gateway builds the stored text from the token itself. */
      description(): string {
        return estimatedReadDescription(
          AuthService.getCurrentUser().username,
          this.calculationDetail,
        )
      },
    },
    watch: {
      /*
        A new estimate is a new question. Any confirmation or outcome on screen
        belongs to the figure it was shown for, not the one now displayed. The
        refetch after a submit rebuilds the same rows, which is not a new
        estimate - keeping the confirmation also stops a second submit of the
        same figure (SO-33700).
      */
      rows(rows: IEstimatorResultRow[], previous: IEstimatorResultRow[]) {
        if (isEqual(rows, previous)) return

        this.confirming = false
        this.submitted = false
        this.errorMessage = ""
      },
    },
    methods: {
      startConfirming() {
        this.errorMessage = this.readings ? "" : NO_METER_MESSAGE
        this.confirming = this.readings !== null
      },
      async submit() {
        const readings = this.readings
        const store = useCurrentAccountStore()
        const billingAccountId = store.coreId.data
        if (!readings || !billingAccountId) return

        this.submitting = true
        this.errorMessage = ""

        try {
          await MeterReadEstimatorService.submitEstimatedRead(
            billingAccountId,
            readings,
            this.calculationDetail,
          )
          this.submitted = true
          this.confirming = false
          await queryClient.invalidateQueries({
            queryKey: BackofficeRestQueries.meterpoints.forAccount(
              store.accountNumber,
            ).queryKey,
          })
        } catch (error) {
          this.errorMessage =
            error instanceof Error && error.message
              ? error.message
              : "The estimated read could not be submitted"
        } finally {
          this.submitting = false
        }
      },
    },
  })
</script>

<style scoped>
  .estimator-submit__confirm {
    border: 1px solid rgb(var(--v-theme-border));
    border-radius: 8px;
    padding: 12px 16px;
  }

  .estimator-submit__read {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  /*
    The description is stored as written, line breaks included, so it is set
    apart as a quoted block - the text Junifer will hold, not instructions.
  */
  .estimator-submit__description {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    font-family: inherit;
    margin: 0;
    padding: 8px 12px;
    border-left: 3px solid rgb(var(--v-theme-border));
    background: rgba(var(--v-theme-on-surface), 0.04);
    border-radius: 4px;
  }
</style>
